import React, { useEffect, useRef, useState } from 'react'

// The one signature pad, shared by every surface that takes a signature (patient intake,
// treatment consent, the dentist's charting sign-off). There were three near-identical copies;
// a rule fixed in one did not reach the others.
//
// Benchmarked 2026-08-12 against e-signature capture guidance. Four things it must do that the
// original copies did not:
//
//   1. REFUSE AN ACCIDENTAL MARK. A 4-pixel brush of the screen signed a treatment consent in
//      testing. Guidance is explicit that defective capture — a dot, a tiny tap, a stray
//      scribble — can invalidate the signature. Minimum ink length, minimum bounding box and
//      minimum signing time are checked before "signed" is reported at all.
//   2. UNDO, SEPARATE FROM CLEAR. Clearing the whole attempt is the single most common source
//      of signature-capture errors; an undo removes the reason to reach for it.
//   3. PALM REJECTION. One active pointer at a time. On a tablet a resting palm otherwise draws
//      through the middle of the signature.
//   4. KEEP THE CAPTURE METADATA, not just the bitmap. Stroke count, ink length, bounding box
//      and duration travel with the image: a stored image alone has weaker evidentiary value
//      than one with the capture record behind it.
//
// The TYPED NAME remains the legally operative signature (02-esignature-standard S-3 #1). This
// is the flourish beside it — but a flourish that is plainly an accident is worse than none.

export const SIGNATURE_RULES = {
  minInkLength: 120,   // px of travel — a signature, not a tap
  minBoxArea: 900,     // px² of bounding box — not a dot
  minDurationMs: 250,  // a deliberate act, not a brush
}

// Exported so the same thresholds can be asserted in a test rather than re-typed.
export function assessSignature(strokes) {
  const pts = strokes.flat()
  if (pts.length < 2) return { ok: false, reason: 'too-short', metrics: null }

  let ink = 0
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
  let t0 = Infinity, t1 = -Infinity

  strokes.forEach((st) => {
    st.forEach((pt, i) => {
      const [x, y, t] = pt
      if (x < minX) minX = x; if (x > maxX) maxX = x
      if (y < minY) minY = y; if (y > maxY) maxY = y
      if (t < t0) t0 = t; if (t > t1) t1 = t
      if (i > 0) {
        const [px, py] = st[i - 1]
        ink += Math.hypot(x - px, y - py)
      }
    })
  })

  const width = Math.max(0, maxX - minX)
  const height = Math.max(0, maxY - minY)
  const metrics = {
    strokes: strokes.length,
    points: pts.length,
    ink_length: Math.round(ink),
    box_width: Math.round(width),
    box_height: Math.round(height),
    duration_ms: Math.max(0, Math.round(t1 - t0)),
  }

  if (ink < SIGNATURE_RULES.minInkLength) return { ok: false, reason: 'too-little-ink', metrics }
  if (width * height < SIGNATURE_RULES.minBoxArea) return { ok: false, reason: 'too-small', metrics }
  if (metrics.duration_ms < SIGNATURE_RULES.minDurationMs) return { ok: false, reason: 'too-quick', metrics }
  return { ok: true, reason: null, metrics }
}

/**
 * onChange({ dataUrl, metrics }) — dataUrl is '' until the mark actually qualifies as a
 * signature, so a caller that only checks truthiness still gets the new behaviour for free.
 */
export default function SignaturePad({ onChange, highlighted = false, label = 'Sign here with your finger' }) {
  const ref = useRef(null)
  const strokes = useRef([])
  const cur = useRef(null)
  const activePointer = useRef(null)   // palm rejection: exactly one pointer draws
  const [status, setStatus] = useState({ hasInk: false, ok: false, reason: null })

  const redraw = () => {
    const cv = ref.current
    if (!cv) return
    const dpr = window.devicePixelRatio || 1
    const ctx = cv.getContext('2d')
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.clearRect(0, 0, cv.offsetWidth, cv.offsetHeight)
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.strokeStyle = '#1f2937'
    ctx.lineWidth = 2.4
    const all = cur.current ? strokes.current.concat([cur.current]) : strokes.current
    all.forEach((st) => {
      if (st.length < 2) return
      ctx.beginPath()
      ctx.moveTo(st[0][0], st[0][1])
      // quadratic midpoints — display smoothing only; the raw points are what we keep
      for (let i = 1; i < st.length - 1; i++) {
        const mx = (st[i][0] + st[i + 1][0]) / 2
        const my = (st[i][1] + st[i + 1][1]) / 2
        ctx.quadraticCurveTo(st[i][0], st[i][1], mx, my)
      }
      ctx.lineTo(st[st.length - 1][0], st[st.length - 1][1])
      ctx.stroke()
    })
  }

  const size = () => {
    const cv = ref.current
    if (!cv) return
    const dpr = window.devicePixelRatio || 1
    cv.width = Math.round(cv.offsetWidth * dpr)
    cv.height = Math.round(cv.offsetHeight * dpr)
    redraw()
  }

  useEffect(() => {
    size()
    window.addEventListener('resize', size)
    return () => window.removeEventListener('resize', size)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const publish = () => {
    const verdict = assessSignature(strokes.current)
    setStatus({ hasInk: strokes.current.length > 0, ok: verdict.ok, reason: verdict.reason })
    onChange(verdict.ok
      ? { dataUrl: ref.current.toDataURL('image/png'), metrics: verdict.metrics }
      : { dataUrl: '', metrics: verdict.metrics })
  }

  const pos = (e) => {
    const r = ref.current.getBoundingClientRect()
    return [e.clientX - r.left, e.clientY - r.top, e.timeStamp || 0]
  }

  const down = (e) => {
    if (activePointer.current !== null) return   // a second contact is a palm, not a pen
    e.preventDefault()
    activePointer.current = e.pointerId
    ref.current.setPointerCapture(e.pointerId)
    cur.current = [pos(e)]
  }
  const move = (e) => {
    if (e.pointerId !== activePointer.current || !cur.current) return
    cur.current.push(pos(e))
    redraw()
  }
  const up = (e) => {
    if (e && e.pointerId !== activePointer.current) return
    activePointer.current = null
    if (!cur.current) return
    if (cur.current.length > 1) strokes.current.push(cur.current)
    cur.current = null
    redraw()
    publish()
  }

  const undo = () => {
    strokes.current.pop()
    redraw()
    publish()
  }
  const clear = () => {
    if (strokes.current.length > 1 && !window.confirm('Clear the whole signature and start again?')) return
    strokes.current = []
    cur.current = null
    redraw()
    publish()
  }

  const HINT = {
    'too-short': 'That looks like a tap rather than a signature — please sign your name.',
    'too-little-ink': 'That is a bit short for a signature — please sign your full name.',
    'too-small': 'That mark is very small — please sign across the box.',
    'too-quick': 'Please take a moment and sign your name.',
  }

  return (
    <div>
      <div
        id={highlighted ? 'intake-highlight' : undefined}
        className={`relative h-44 rounded-xl border-2 border-dashed bg-white ${
          highlighted ? 'border-red-400 ring-2 ring-red-200'
            : status.ok ? 'border-emerald-400' : 'border-slate-300'}`}
      >
        <canvas
          ref={ref}
          style={{ touchAction: 'none' }}
          className="absolute inset-0 h-full w-full"
          onPointerDown={down} onPointerMove={move}
          onPointerUp={up} onPointerCancel={up} onPointerLeave={up}
        />
        {!status.hasInk && (
          <span className="pointer-events-none absolute inset-x-0 bottom-4 text-center text-sm text-slate-400">
            {label}
          </span>
        )}
      </div>

      <div className="mt-2 flex items-center gap-3">
        <button type="button" onClick={undo} disabled={!status.hasInk}
          className="text-xs font-semibold text-slate-500 underline disabled:opacity-40">
          Undo last stroke
        </button>
        <button type="button" onClick={clear} disabled={!status.hasInk}
          className="text-xs font-semibold text-slate-500 underline disabled:opacity-40">
          Clear
        </button>
        {status.hasInk && !status.ok && (
          <span className="ml-auto text-xs font-medium text-amber-700">{HINT[status.reason] || 'Please sign your name.'}</span>
        )}
      </div>
    </div>
  )
}
